import type { ChangeEvent } from 'react'
import { memo } from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/interactives/Tooltip.jsx'
import { faUnlock, faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'

import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'

import {
  cueUiActions,
  useIsCueSelected,
  useSelectedCueIds,
} from './cue-ui.store'
import { getIndexedCue, getSortedCues } from './cue.derived'
import { CueDragHandle } from './CueDragHandle'
import { CueSettingsMenu } from './menu/CueSettingsMenu'
import type { AddCueCallback, UpdateCueCallback } from './menu/menu.types'

type CueControlsProps = {
  cue: Cue
  readonly: boolean
  parentId?: string | null
  onUpdateCue: UpdateCueCallback
  onAddCue: AddCueCallback
  onDuplicateCue: () => void
  onDelete: () => void
  onJumpToCue: () => void
  onSetNext: () => void
}

export const CueControls = memo(function CueControls({
  cue,
  readonly,
  parentId,
  onUpdateCue,
  onAddCue,
  onDuplicateCue,
  onDelete,
  onJumpToCue,
  onSetNext,
}: CueControlsProps) {
  const selectedCues = useSelectedCueIds()
  const isCueSelected = useIsCueSelected(cue.id)
  const isParentCueSelected = useIsCueSelected(parentId)

  /**
   * Handles the selection of cues in the rundown.
   *
   * - If the shift key is held during selection, selects a range of cues from the
   *   last selected cue to the current one. If any group cues are selected in
   *   this range, their child cues are removed from the selection.
   * - If selecting a group cue directly, removes any selected children cues of
   *   that group and selects the group cue itself.
   * - Otherwise, toggles the selected state of the individual cue.
   *
   * @param event - The selection event.
   */
  function onSelectCue(event: ChangeEvent<HTMLInputElement>) {
    // Check if shift key is held to perform range selection
    if ((event.nativeEvent as MouseEvent).shiftKey) {
      // Read imperatively: the cue list changes on every edit, and subscribing
      // here would re-render the row on changes this handler does not use.
      const sortedCues = getSortedCues()
      const sortedCueIds = sortedCues.map((c) => c.id)
      const lastSelectedId = selectedCues[selectedCues.length - 1]
      const lastSelectedIndex = lastSelectedId
        ? sortedCueIds.indexOf(lastSelectedId)
        : -1
      const currentIndex = sortedCueIds.indexOf(cue.id)

      // Determine the start and end indices for the range selection
      const start = Math.min(lastSelectedIndex, currentIndex)
      const end = Math.max(lastSelectedIndex, currentIndex)
      const newSelectedIds = sortedCueIds.slice(start, end + 1)

      // Combine current selections with the new range, removing duplicates
      const updatedSelectedCues = Array.from(
        new Set([...selectedCues, ...newSelectedIds]),
      )

      // If any group cues are selected, remove their child cues
      const selectedChildren = updatedSelectedCues.flatMap(
        (id) => getIndexedCue(id)?.children?.map((child) => child.id) ?? [],
      )
      const selectedWithoutChildren = updatedSelectedCues.filter(
        (id) => !selectedChildren.includes(id),
      )

      cueUiActions.setSelected(selectedWithoutChildren)
    } else {
      // Handle individual cue selection or group cue selection
      if (event.target.checked && cue.type === CueType.GROUP) {
        // Remove any selected children cues of this group and select the group cue
        const childrenCues =
          getIndexedCue(cue.id)?.children?.map((child) => child.id) ?? []
        const selectedWithoutChildren = selectedCues.filter(
          (id) => !childrenCues.includes(id),
        )
        cueUiActions.setSelected([...selectedWithoutChildren, cue.id])
      } else {
        // Toggle the selected state of the individual cue
        cueUiActions.toggleSelected(cue.id)
      }
    }
  }

  if (readonly) return null

  return (
    <div
      className={cn(
        'sticky top-27 flex flex-col group-hover:backdrop-blur-sm min-h-14 rounded-sm',
        (isParentCueSelected || isCueSelected) && 'bg-blue-700/90',
        selectedCues.length > 0 ? 'h-full' : '', // Force full height when any items are selected to ensure background stretches to full height
      )}
    >
      {/* Drag Handle */}
      {cue.settings?.preventEdits || selectedCues.length > 0 ? (
        <span className="h-6"></span>
      ) : (
        <CueDragHandle />
      )}

      <CueSettingsMenu
        cue={cue}
        parentId={parentId}
        onAddCue={onAddCue}
        onUpdateCue={onUpdateCue}
        onDuplicateCue={onDuplicateCue}
        onDelete={onDelete}
        onJumpToCue={onJumpToCue}
        onSetNext={onSetNext}
      />

      {/* Lock Button OR Collapse button */}
      {cue.settings?.preventEdits ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="flex-none w-7 h-7 rounded-sm group-hover:opacity-70 hover:opacity-100! transition-opacity backdrop-blur-sm text-xs focus:outline-hidden focus:ring-3 group/lock opacity-70 text-yellow-500"
              onClick={() => onUpdateCue({ 'settings.preventEdits': false })}
            >
              <span className="hidden group-hover/lock:inline">
                <FontAwesomeIcon
                  icon={faUnlock}
                  size="sm"
                />
              </span>
              <span className="group-hover/lock:hidden">
                <FontAwesomeIcon
                  icon={faLock}
                  size="sm"
                />
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent>Allow cue editing</TooltipContent>
        </Tooltip>
      ) : (
        !isParentCueSelected && (
          <Tooltip>
            <TooltipTrigger>
              <div className="flex flex-col justify-center align-middle w-full h-7 rounded-sm">
                <label
                  htmlFor={`select-${cue.id}`}
                  className={cn('mx-auto p-1.5 items-center')}
                >
                  <input
                    type="checkbox"
                    checked={isCueSelected}
                    onChange={onSelectCue}
                    id={`select-${cue.id}`}
                    className={[
                      'mx-auto group-hover:opacity-70 hover:opacity-100! transition-opacity text-xs focus:outline-hidden focus:ring-3',
                      isCueSelected ? 'opacity-70' : 'opacity-0',
                    ].join(' ')}
                  />
                </label>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {isCueSelected ? 'Deselect' : 'Select'}{' '}
              {cue.type === CueType.GROUP ? 'group' : 'cue'}
            </TooltipContent>
          </Tooltip>
        )
      )}
    </div>
  )
})
